import { FormApi } from "final-form";
import _ from "lodash";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import i18n from "src/i18n/config";
import { useUpdateProductMutation } from "src/ui/containers/dashboard/products/api";
import { Modal } from "src/ui/shared/Modal/Modal";
import { useValidationSchema } from "src/ui/utils/useValidationSchema";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";

import { AndroidModule, Product } from "@dashboard/products/types";
import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { PermissionService } from "@services/permissionService/permissionService";
import { PrimaryButton } from "@shared/CustomButton";
import { TextField } from "@shared/form/TextField";
import { Tooltip } from "@shared/Tooltip";

const schema = z.object({
    name: z.string({ message: i18n.t("common:validation.fieldRequired") }),
});

const useStyles = makeStyles()((theme) => ({
    form: { display: "flex", flexDirection: "column", gap: theme.spacing(2), marginTop: theme.spacing(3) },
}));

type FormValues = {
    name: string;
};

type Props = {
    product: Product | null;
    open: boolean;
    onToggle: (value: boolean) => void;
};

export const ExcludeModuleDialog = (props: Props) => {
    const [updateProduct] = useUpdateProductMutation();

    const { classes } = useStyles();
    const { t } = useTranslation(["pages", "modals", "common", "sidepanels"]);

    const modules = props.product?.customBuildConfig?.modules || [];

    const validate = useValidationSchema(schema);

    const { data: workspace } = useCurrentWorkspace();
    const { productAbility } = PermissionService();
    const cannotManageProducts = productAbility(workspace).cannot("manage", "Product");
    const formDisabled = cannotManageProducts || !props.product;

    const onSubmit = async (values: FormValues, form: FormApi<FormValues>) => {
        if (!props.product) {
            return;
        }
        const newProduct = _.cloneDeep(props.product);
        const newModule: AndroidModule = {
            type: "remove_override",
            name: values.name,
            tags: ["System"],
        };
        if (newProduct.customBuildConfig) {
            newProduct.customBuildConfig.modules = [...modules, newModule];
        }
        await updateProduct(newProduct);
        form.restart();
        props.onToggle(false);
    };
    const getTooltipTitle = () => {
        if (cannotManageProducts) {
            return t("common:tooltip.cannotManageProducts");
        }
        if (!props.product) {
            return t("common:tooltip.noProductSelected");
        }
        return undefined;
    };

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            validate={validate}
            render={({ handleSubmit, form, submitting }) => (
                <Modal
                    open={props.open}
                    onClose={() => props.onToggle(false)}
                    title={t("modals:manageProductModules.excludeModule.title")}
                    endButton={
                        <Tooltip hide={!formDisabled} title={getTooltipTitle()}>
                            <PrimaryButton onClick={form.submit} disabled={formDisabled || submitting} loading={submitting}>
                                {t("common:button.add")}
                            </PrimaryButton>
                        </Tooltip>
                    }
                    description={t("modals:manageProductModules.excludeModule.description")}
                >
                    <form onSubmit={handleSubmit} className={classes.form}>
                        <TextField name="name" label={t("common:label.name")} />
                    </form>
                </Modal>
            )}
        />
    );
};
